import { useState, useEffect } from 'react'
import classnames from 'classnames'

const Table = ({
  columns,
  fetchData,
  loading = false,
  itemsPerPage = 10,
}: {
  columns: { key: string; name: string; width?: string; render: Function }[]
  loading?: boolean
  fetchData: Function
  itemsPerPage?: number
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [items, setItems] = useState<any>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  useEffect(() => {
    const handleFetchData = async () => {
      const { data, totalCount } = await fetchData(currentPage)

      setItems(data)
      setTotalCount(totalCount)
    }

    handleFetchData()
  }, [fetchData, currentPage])

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage

  const nextPage = async () => {
    if (currentPage < Math.ceil(totalCount / itemsPerPage)) {
      setCurrentPage(currentPage + 1)
    }
  }

  const prevPage = async () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <div className="inline-block w-full p-1.5 align-middle">
          <div className="overflow-hidden rounded-lg">
            <table className="hidden w-full table-fixed divide-y divide-white divide-opacity-20 md:table">
              <thead>
                <tr>
                  {columns.map(
                    ({ key, name, width }: { key: string; name: string; width?: string }) => (
                      <th
                        key={key}
                        scope="col"
                        className="px-2 pb-4 text-left text-sm font-normal text-white"
                        style={{ width: width || 'auto' }}>
                        {name}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item: {}, key: string) => (
                  <tr key={key}>
                    {columns.map((values, key: number) => (
                      <td className="px-2 pt-4 text-sm font-medium text-white" key={key}>
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {columns[key].render(item)}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="md:hidden">
              {items.map((item: {}, key: string) => (
                <div
                  key={key}
                  className="border-b border-white border-opacity-20 py-5 first:border-t">
                  {columns.map(({ name }, key: number) => (
                    <div
                      className="flex items-center gap-1 px-2 py-1 text-sm font-medium text-white"
                      key={key}>
                      <span className="flex-[0_0_128px] font-bold">{name}: </span>
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {columns[key].render(item)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {loading && (
              <div className="w-full text-center">
                <span className="mx-auto inline-block h-12 w-12 animate-spin rounded-full border-2 border-solid border-main-blue border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"></span>
              </div>
            )}
            {Math.ceil(totalCount / itemsPerPage) > 1 && (
              <div className="mt-6 flex items-center justify-end gap-2 text-white">
                <span
                  className={classnames(
                    'icon-arrow-left cursor-pointer text-xl text-white hover:text-main-blue',
                    {
                      'pointer-events-none text-opacity-50': currentPage === 1,
                    },
                  )}
                  onClick={prevPage}
                />
                {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, totalCount)} of {totalCount}
                <span
                  className={classnames(
                    'icon-arrow-right cursor-pointer text-xl text-white hover:text-main-blue',
                    {
                      'pointer-events-none text-opacity-50':
                        currentPage === Math.ceil(totalCount / itemsPerPage),
                    },
                  )}
                  onClick={nextPage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Table
